import { useState } from 'react';
import { Alert } from 'react-native';
import {
  Button,
  H1,
  H2,
  Input,
  Paragraph,
  Spinner,
  Text,
  TextArea,
  XStack,
  YStack,
} from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { Project } from '@mrrmlab/shared-types';
import {
  useCreateProject,
  useDeleteProject,
  useProjects,
  useUpdateProject,
} from '../hooks';

function parseSteps(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function ProjectsSettingsScreen() {
  const list = useProjects();
  const create = useCreateProject();
  const update = useUpdateProject();
  const del = useDeleteProject();

  const [title, setTitle] = useState('');
  const [stepsRaw, setStepsRaw] = useState('');

  const steps = parseSteps(stepsRaw);
  const canSubmit = title.trim().length > 0 && steps.length >= 1 && steps.length <= 50;

  const onAdd = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        title: title.trim(),
        steps: steps.map((s) => ({ title: s })),
      },
      {
        onSuccess: () => {
          setTitle('');
          setStepsRaw('');
        },
      },
    );
  };

  return (
    <Screen scrollable>
      <YStack gap="$2">
        <H1>Projekte</H1>
        <Text fontSize="$3" opacity={0.7}>
          Mehrstufige Vorhaben mit einer Schritt-Reihenfolge. Jeder Schritt wird zu einem
          Tagesplan-Eintrag — der nächste Schritt erscheint erst, wenn der vorherige erledigt
          ist.
        </Text>
      </YStack>

      <YStack
        gap="$3"
        padding="$4"
        borderRadius="$4"
        borderWidth={1}
        borderColor="$borderColor"
        backgroundColor="$color2"
      >
        <H2 fontSize="$6">Projekt anlegen</H2>
        <YStack gap="$2">
          <Text fontSize="$2">Titel</Text>
          <Input
            placeholder="z.B. Garage aufräumen"
            value={title}
            onChangeText={setTitle}
            accessibilityLabel="Projekt-Titel"
          />
        </YStack>
        <YStack gap="$2">
          <Text fontSize="$2">Schritte (eine Zeile pro Schritt)</Text>
          <TextArea
            placeholder={'Boden ausräumen\nRegale einbauen\nAlles einräumen'}
            value={stepsRaw}
            onChangeText={setStepsRaw}
            numberOfLines={6}
            minHeight={140}
            autoCapitalize="sentences"
            accessibilityLabel="Projekt-Schritte"
          />
          <Text fontSize="$1" opacity={0.6}>
            {steps.length} Schritt{steps.length === 1 ? '' : 'e'}
          </Text>
        </YStack>
        {create.isError ? (
          <Text fontSize="$2" color="$red10">
            {create.error.message}
          </Text>
        ) : null}
        <Button
          theme="active"
          onPress={onAdd}
          disabled={create.isPending || !canSubmit}
          accessibilityLabel="Projekt hinzufügen"
        >
          {create.isPending ? 'Speichere…' : 'Hinzufügen'}
        </Button>
      </YStack>

      <YStack gap="$2">
        <H2 fontSize="$6">Aktive Projekte</H2>
        {list.isPending ? (
          <Spinner />
        ) : list.isError ? (
          <Paragraph color="$red10">
            Konnte Projekte nicht laden: {list.error.message}
          </Paragraph>
        ) : (list.data ?? []).length === 0 ? (
          <Paragraph opacity={0.7}>Noch kein Projekt angelegt.</Paragraph>
        ) : (
          <YStack gap="$2">
            {(list.data ?? []).map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                onDelete={() => del.mutate(project.id)}
                onToggleArchive={() =>
                  update.mutate({ id: project.id, body: { archived: !project.archivedAt } })
                }
                deleting={del.isPending && del.variables === project.id}
                updating={
                  update.isPending && update.variables?.id === project.id
                }
              />
            ))}
          </YStack>
        )}
      </YStack>
    </Screen>
  );
}

function ProjectRow({
  project,
  onDelete,
  onToggleArchive,
  deleting,
  updating,
}: {
  project: Project;
  onDelete: () => void;
  onToggleArchive: () => void;
  deleting: boolean;
  updating: boolean;
}) {
  const archived = project.archivedAt !== null;

  const confirmDelete = () => {
    Alert.alert(
      'Projekt löschen',
      `"${project.title}" und alle ${project.steps.length} Schritte wirklich löschen?`,
      [
        { text: 'Abbrechen', style: 'cancel' },
        { text: 'Löschen', style: 'destructive', onPress: onDelete },
      ],
    );
  };

  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      opacity={archived ? 0.55 : 1}
    >
      <YStack gap="$1">
        <Text fontSize="$5" fontWeight="600">
          {project.title}
        </Text>
        <Text fontSize="$2" opacity={0.6}>
          {project.steps.length} Schritt{project.steps.length === 1 ? '' : 'e'}
          {archived ? ' · archiviert' : ''}
        </Text>
        <YStack gap="$0" paddingLeft="$2">
          {[...project.steps]
            .sort((a, b) => a.position - b.position)
            .map((s, i) => (
              <Text key={s.id} fontSize="$2" opacity={0.75}>
                {i + 1}. {s.title}
              </Text>
            ))}
        </YStack>
      </YStack>
      <XStack gap="$2">
        <Button
          size="$3"
          chromeless
          onPress={onToggleArchive}
          disabled={updating}
          accessibilityLabel={
            archived ? `Reaktivieren: ${project.title}` : `Archivieren: ${project.title}`
          }
        >
          {updating ? '…' : archived ? 'Reaktivieren' : 'Archivieren'}
        </Button>
        <Button
          size="$3"
          chromeless
          color="$red10"
          onPress={confirmDelete}
          disabled={deleting}
          accessibilityLabel={`Entfernen: ${project.title}`}
        >
          {deleting ? 'Lösche…' : 'Entfernen'}
        </Button>
      </XStack>
    </YStack>
  );
}
